import axios from "axios";
import { useState } from "react";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../utils/constants";
import { getEmbedToken } from "../api/api";

axios.interceptors.request.use((config) => {
  const token = getEmbedToken() || localStorage.getItem("authToken") || "";

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

function parseErrorMessage(error) {
  try {
    // FastAPI error format: { detail: "message" } or { detail: [...] }
    if (error?.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail) && detail.length > 0) {
        return detail[0]?.msg || JSON.stringify(detail[0]);
      }
      return JSON.stringify(detail);
    }
    // Frappe legacy format (kept for backward compatibility during migration)
    const errorArray = JSON.parse(error?.response?.data?._server_messages);

    if (Array.isArray(errorArray) && errorArray.length > 0) {
      const firstErrorObj = JSON.parse(errorArray[0]);

      if (firstErrorObj && firstErrorObj.message) {
        return firstErrorObj.message;
      }
    }

    return error?.response?.data?.message;
  } catch (e) {
    return error?.response?.data?.message || "Error Posting Data";
  }
}

const useAxios = (method, url = "", options = {}) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const sendRequest = async ({
    customURL = "",
    payload = {},
    headers = {},
    cb = () => {},
    showLoading = true,
  } = {}) => {
    const apiOptions = {
      auth: true,
      showAlert: true,
      useBaseURL: true,
      ...options,
    };

    setLoading(showLoading ?? true);

    const resolvedURL = customURL || url;
    const fullURL = `${apiOptions.useBaseURL ? API_BASE_URL : ""}${resolvedURL}`;

    const config = {
      method,
      url: fullURL,
      headers,
      ...(method !== "get" && { data: payload }),
    };

    try {
      const response = await axios(config);
      setData(response.data);
      cb(response.data);
    } catch (err) {
      if (err.code === "ERR_NETWORK") {
        toast("It seems our servers are currently unresponsive.", {
          type: "error",
        });

        return;
      }

      let errorMessage = parseErrorMessage(err);

      setError(err);
      cb(null, errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return [sendRequest, { data, loading, error }, setData];
};

export const useAxiosGet = (url = "", options = {}) =>
  useAxios("get", url, options);

export const useAxiosPost = (url = "", options = {}) =>
  useAxios("post", url, options);

export const useAxiosPatch = (url = "", options = {}) =>
  useAxios("patch", url, options);

export const useAxiosPut = (url = "", options = {}) =>
  useAxios("put", url, options);

export const useAxiosDelete = (url = "", options = {}) =>
  useAxios("delete", url, options);
